/**
 * cashflow.ts — Domínio de Fluxo de Caixa [CORE]
 *
 * Regras fundamentais:
 *   - Valores SEMPRE em centavos (inteiros). NUNCA float. Regra de ouro Peter.
 *   - Cada CashflowEntry é rastreável até um SourceRecord (ADR-007).
 *   - Regimes explícitos em todo campo numérico (UX-FINANCEIRO §1.3).
 *
 * Referências:
 *   - UX-FINANCEIRO.md §Módulo 1 (6 entradas + 11 saídas hospitalares)
 *   - FINANCEIRO.md §1 (6 regimes), §2 (estrutura Royal), §10 (presets)
 *   - ADR-007 §1–2 (proveniência, cadeia de auditabilidade)
 */

import { z } from "zod";
import { MoneySchema } from "./money";

// ---------------------------------------------------------------------------
// Regimes de valor — seis categorias (UX-FINANCEIRO §1.3, FINANCEIRO §1)
// ---------------------------------------------------------------------------

export const RegimenSchema = z.enum([
  "realizado",    // Transitou em conta; baixado no ERP — badge verde sólido
  "contratado",   // Compromisso jurídico firmado, não realizado — badge azul
  "provisionado", // Reconhecido por competência; não pago — badge lilás
  "projetado",    // Estimativa com premissas explícitas — badge ciano
  "meta",         // Valor-alvo do orçamento aprovado — badge âmbar
  "cenario",      // Variação paramétrica sobre o Projetado — badge cinza
]);

export type Regimen = z.infer<typeof RegimenSchema>;

// Cores de apresentação por regime (ADR-007 §2, UX-FINANCEIRO §1.3)
export const REGIMEN_COLORS: Record<Regimen, string> = {
  realizado:    "#16a34a", // verde sólido
  contratado:   "#1e40af", // azul escuro
  provisionado: "#7c3aed", // lilás
  projetado:    "#0891b2", // ciano
  meta:         "#d97706", // âmbar
  cenario:      "#6b7280", // cinza
};

export const REGIMEN_LABELS: Record<Regimen, string> = {
  realizado:    "Realizado",
  contratado:   "Contratado",
  provisionado: "Provisionado",
  projetado:    "Projetado",
  meta:         "Meta",
  cenario:      "Cenário",
};

// ---------------------------------------------------------------------------
// Cenários de simulação (FINANCEIRO §10.2, UX-FINANCEIRO §Cenários)
// ---------------------------------------------------------------------------

export const ScenarioSchema = z.enum([
  "base",        // Premissas correntes (~90d defasagem, % glosa estimada)
  "pessimista",  // Entradas_Operadora * 0,70; Juros * 1,20
  "otimista",    // Entradas_Operadora * 1,15; Glosa * 0,70
  "stress",      // Entradas_Operadora * 0,50; Juros * 1,50
]);

export type Scenario = z.infer<typeof ScenarioSchema>;

export const SCENARIO_LABELS: Record<Scenario, string> = {
  base:       "Base",
  pessimista: "Pessimista",
  otimista:   "Otimista",
  stress:     "Estresse",
};

// ---------------------------------------------------------------------------
// Horizonte temporal (UX-FINANCEIRO §Estrutura da tela, FINANCEIRO §10.1)
// ---------------------------------------------------------------------------

export const HorizonSchema = z.enum([
  "13semanas", // D+0 a D+90, granularidade semanal
  "12meses",   // M+0 a M+11, granularidade mensal
]);

export type Horizon = z.infer<typeof HorizonSchema>;

export const HORIZON_LABELS: Record<Horizon, string> = {
  "13semanas": "13 Semanas",
  "12meses":   "12 Meses",
};

// ---------------------------------------------------------------------------
// Tipo de bloco — entrada ou saída
// ---------------------------------------------------------------------------

export const BlockTypeSchema = z.enum(["entrada", "saida"]);
export type BlockType = z.infer<typeof BlockTypeSchema>;

// ---------------------------------------------------------------------------
// CashflowBlock — configuração de uma categoria de fluxo
// (UX-FINANCEIRO: 6 entradas + 11 saídas hospitalares)
// ---------------------------------------------------------------------------

export const CashflowBlockSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  tipo: BlockTypeSchema,
  /** true = incluído no cálculo; false = excluído (toggle) */
  ativo: z.boolean(),
  /** Regime predominante das entradas deste bloco */
  regimen: RegimenSchema,
  /**
   * Descrição do vencimento/prazo típico (ex.: "dia 5", "negociado").
   * Presente apenas em blocos de saída.
   */
  vencimentoDescricao: z.string().optional(),
  /**
   * Fatores de ajuste por cenário [0,∞) multiplicados sobre o valor base.
   * 1.0 = sem ajuste (cenário base).
   * Configurável por tenant em config/scenarioPresets.
   */
  scenarioFactors: z.object({
    base:       z.number().nonnegative(),
    pessimista: z.number().nonnegative(),
    otimista:   z.number().nonnegative(),
    stress:     z.number().nonnegative(),
  }).optional(),
});

export type CashflowBlock = z.infer<typeof CashflowBlockSchema>;

// ---------------------------------------------------------------------------
// Blocos padrão (6 entradas + 11 saídas — contexto hospitalar Royal)
// UX-FINANCEIRO §Estrutura da tela; FINANCEIRO §2.1–2.2
// ---------------------------------------------------------------------------

export const DEFAULT_CASHFLOW_BLOCKS: CashflowBlock[] = [
  // ---- ENTRADAS ----
  {
    id: "receb-operadoras",
    label: "Recebimentos de Operadoras",
    tipo: "entrada",
    ativo: true,
    regimen: "projetado",
    scenarioFactors: { base: 1.0, pessimista: 0.70, otimista: 1.15, stress: 0.50 },
  },
  {
    id: "receb-particular",
    label: "Particular / Co-participação",
    tipo: "entrada",
    ativo: true,
    regimen: "projetado",
    scenarioFactors: { base: 1.0, pessimista: 0.85, otimista: 1.10, stress: 0.65 },
  },
  {
    id: "receb-sus",
    label: "SUS",
    tipo: "entrada",
    ativo: true,
    regimen: "projetado",
    scenarioFactors: { base: 1.0, pessimista: 0.95, otimista: 1.05, stress: 0.80 },
  },
  {
    id: "receb-home-care",
    label: "Home Care",
    tipo: "entrada",
    ativo: true,
    regimen: "projetado",
    scenarioFactors: { base: 1.0, pessimista: 0.80, otimista: 1.10, stress: 0.60 },
  },
  {
    id: "antecipacao-recebiveis",
    label: "Antecipação de Recebíveis",
    tipo: "entrada",
    ativo: false,
    regimen: "contratado",
    scenarioFactors: { base: 1.0, pessimista: 0.70, otimista: 1.0, stress: 0.50 },
  },
  {
    id: "aportes-captacao",
    label: "Aportes / Captação de Crédito",
    tipo: "entrada",
    ativo: false,
    regimen: "projetado",
    scenarioFactors: { base: 1.0, pessimista: 0.50, otimista: 1.20, stress: 0.0 },
  },
  // ---- SAÍDAS ----
  {
    id: "folha-encargos",
    label: "Folha + Encargos CLT",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "dia 5",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 1.0, stress: 1.05 },
  },
  {
    id: "terceirizados-pesados",
    label: "Terceirizados Pesados",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "dia 15",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 1.0, stress: 1.05 },
  },
  {
    id: "medicos",
    label: "Médicos",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "dia 25",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 1.0, stress: 1.05 },
  },
  {
    id: "outros-terceirizados",
    label: "Outros Terceirizados",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "dia 20",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 1.0, stress: 1.05 },
  },
  {
    id: "home-care-saidas",
    label: "Home Care (Saídas)",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "variável",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 1.0, stress: 1.05 },
  },
  {
    id: "fornecedores-opme",
    label: "Fornecedores OPME/Farmácia",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "negociado",
    scenarioFactors: { base: 1.0, pessimista: 1.05, otimista: 0.95, stress: 1.10 },
  },
  {
    id: "fornecedores-dieta",
    label: "Fornecedores Dieta/Outros",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "negociado",
    scenarioFactors: { base: 1.0, pessimista: 1.05, otimista: 0.95, stress: 1.10 },
  },
  {
    id: "financeiro-juros",
    label: "Financeiro / Juros",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "contratual",
    scenarioFactors: { base: 1.0, pessimista: 1.20, otimista: 1.0, stress: 1.50 },
  },
  {
    id: "capex-residual",
    label: "Capex em Andamento (residual)",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "contratual",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 1.0, stress: 1.0 },
  },
  {
    id: "tributos",
    label: "Tributos",
    tipo: "saida",
    ativo: true,
    regimen: "provisionado",
    vencimentoDescricao: "competência",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 1.0, stress: 1.0 },
  },
  {
    id: "ti-sistemas",
    label: "TI / Sistemas",
    tipo: "saida",
    ativo: true,
    regimen: "contratado",
    vencimentoDescricao: "contratual",
    scenarioFactors: { base: 1.0, pessimista: 1.0, otimista: 0.95, stress: 1.0 },
  },
];

// ---------------------------------------------------------------------------
// CashflowEntry — lançamento individual (rastreável até SourceRecord)
// ADR-007 §2: cada entry preserva proveniência até a fonte
// ---------------------------------------------------------------------------

export const CashflowEntrySchema = z.object({
  id: z.string().min(1),
  /** Bloco ao qual este lançamento pertence */
  blockId: z.string().min(1),
  /** Identificador da unidade (facilityId ou "consolidado") */
  facilityId: z.string().min(1),
  /**
   * Valor em centavos (Money).
   * Entradas: positivo. Saídas: positivo (direção determinada pelo blockId/tipo).
   */
  valor: MoneySchema,
  /** Data de competência/vencimento — ISO 8601 date (YYYY-MM-DD) */
  data: z.string().date(),
  /** Regime explícito do valor */
  regimen: RegimenSchema,
  /**
   * Referência ao(s) SourceRecord(s) de origem — cadeia de auditabilidade (ADR-007 §2).
   * Array vazio = lançamento manual sem vínculo a ingestão.
   */
  sourceRecordIds: z.array(z.string()),
  /**
   * Indica se este lançamento é de natureza intercompany (transito entre unidades).
   * Lançamentos intercompany são eliminados na consolidação.
   */
  intercompany: z.boolean().optional(),
  /** Observação livre — descritivo do lançamento */
  descricao: z.string().optional(),
});

export type CashflowEntry = z.infer<typeof CashflowEntrySchema>;

// ---------------------------------------------------------------------------
// Período de projeção — janela temporal de um período (semana ou mês)
// ---------------------------------------------------------------------------

export const PeriodSchema = z.object({
  /** Rótulo do período (ex.: "Sem 1", "Set/2026") */
  label: z.string(),
  /** Data de início do período (ISO 8601) */
  startDate: z.string().date(),
  /** Data de fim do período (ISO 8601) */
  endDate: z.string().date(),
});

export type Period = z.infer<typeof PeriodSchema>;

// ---------------------------------------------------------------------------
// Janelas de aging — classificação temporal de recebíveis
// (cash-flow-windows.ts do ALADDIN — REUTILIZAR conceitualmente)
// ---------------------------------------------------------------------------

export const AgingWindowSchema = z.enum([
  "a_vencer",  // ainda não venceu
  "0_30d",     // 1–30 dias de atraso
  "31_90d",    // 31–90 dias de atraso
  "91_180d",   // 91–180 dias de atraso
  "181_360d",  // 181–360 dias de atraso
  "acima_360d", // acima de 360 dias
]);

export type AgingWindow = z.infer<typeof AgingWindowSchema>;

// ---------------------------------------------------------------------------
// SaldoProjetado — resultado do cálculo por período
// Inclui informações de descasamento para o "mapa de descasamento"
// ---------------------------------------------------------------------------

export const SaldoProjetadoSchema = z.object({
  period: PeriodSchema,
  saldoInicial: MoneySchema,
  totalEntradas: MoneySchema,
  totalSaidas: MoneySchema,
  saldoFinal: MoneySchema,
  /** true = período com saldo negativo — destaque no mapa de descasamento */
  descasamento: z.boolean(),
  /** Breakdown por bloco (id do bloco → valor em centavos) */
  entradasPorBloco: z.record(z.string(), z.number().int()),
  saidasPorBloco: z.record(z.string(), z.number().int()),
  /** Regime predominante dos valores do período */
  regimen: RegimenSchema,
});

export type SaldoProjetado = z.infer<typeof SaldoProjetadoSchema>;
